import { z } from "zod";
import AdditionalTourConditions from "./AdditionalTourConditions";
import ChangesAndOrganizerInfo from "./ChangesAndOrganizerInfo";
import OrganizedTourContract from "./OrganizedTourContract";
import styles from "./TransportAgreement.module.scss";

const nullableString = z.string().nullable().optional();

const transportAgreementBookingSchema = z
  .object({
    created: z.string(),
    paymentAmount: z.number().optional(),
    amount: z.number().optional(),
    currency: z.string(),
    payments: z.array(
      z.object({
        state: z.string(),
        amount: z.number(),
      }),
    ),
    paidAmount: z
      .object({
        amount: z.number(),
        currency: z.string(),
      })
      .nullable()
      .optional(),
    details: z.array(
      z.object({
        quantity: z.number(),
      }),
    ),
    pickupLocation: z
      .object({
        name: z.string(),
        address: z.string(),
        query: z.object({
          placeId: nullableString,
          geoLocation: z
            .object({
              lat: z.number(),
              lng: z.number(),
            })
            .nullable()
            .optional(),
        }),
      })
      .nullable()
      .optional(),
    group: z.object({
      time: z.string(),
      date: z.string(),
      customGroupItinerary: z
        .array(
          z.object({
            id: z.string(),
            stepDescription: z.string(),
            stepOrder: z.number(),
          }),
        )
        .nullable()
        .optional(),
      product: z.object({
        isCustomTour: z.boolean().nullable().optional(),
        minimumInGroup: z.number().nullable().optional(),
        meetingPlaceGoogleMapsUrl: nullableString,
        translation: z.object({
          name: z.string(),
          itinerary: nullableString,
          meetingPlaceName: nullableString,
          meetingPlaceDescription: nullableString,
        }),
      }),
    }),
  })
  .refine(
    (d) => d.paymentAmount != null || d.amount != null,
    "Either paymentAmount or amount must be provided",
  )
  .transform(({ amount, paymentAmount, ...rest }) => ({
    ...rest,
    paymentAmount: (paymentAmount ?? amount) as number,
  }));

export type TransportAgreementBooking = z.infer<
  typeof transportAgreementBookingSchema
>;

export function hasTransportParams(
  booking: unknown,
): booking is TransportAgreementBooking {
  return transportAgreementBookingSchema.safeParse(booking).success;
}

export type TransportAgreementProps = {
  booking: TransportAgreementBooking;
};

export default function TransportAgreementDocument({
  booking,
}: TransportAgreementProps) {
  return (
    <div className={styles.TransportAgreementContainer}>
      <OrganizedTourContract booking={booking} />
      <AdditionalTourConditions />
      <ChangesAndOrganizerInfo />
    </div>
  );
}
